import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { ScrollView, YStack } from "tamagui";
import { z } from "zod";

import { DosespotMedication } from "@/api/generated/models";
import {
  Button,
  Footer,
  Heading,
  Image,
  Paragraph,
  RadioGroup,
  useImageAspectRatio,
} from "@/components";
import { BLUR_HASH } from "@/theme/constants";

const prescriptionSchema = z.object({
  selectedId: z.string().min(1, "Please select a medication"),
});

type PrescriptionFormData = z.infer<typeof prescriptionSchema>;

interface PrescriptionUnreadableProps {
  imageUri: string;
  medications: DosespotMedication[];
  onContinue: (medication: DosespotMedication) => void;
  isContinuePending?: boolean;
  onTryAgain: () => void;
}

export const PrescriptionUnreadable = ({
  imageUri,
  medications,
  onContinue,
  isContinuePending,
  onTryAgain,
}: PrescriptionUnreadableProps) => {
  const { control, handleSubmit } = useForm<PrescriptionFormData>({
    resolver: zodResolver(prescriptionSchema),
    defaultValues: { selectedId: "" },
  });
  const { aspectRatio, onLoad } = useImageAspectRatio();

  const handleContinue = handleSubmit(({ selectedId }) => {
    const selectedMedication = medications.find(
      (m) => String(m.prescription_details.dispensable_drug_id) === selectedId,
    );
    if (selectedMedication) onContinue(selectedMedication);
  });

  return (
    <YStack flex={1}>
      <ScrollView
        flex={1}
        contentContainerStyle={{
          padding: "$200",
          gap: "$200",
        }}
      >
        <Image
          source={{ uri: imageUri }}
          width="100%"
          maxWidth={400}
          aspectRatio={aspectRatio ?? 1.6}
          borderRadius="$150"
          backgroundColor="$neutral900"
          overflow="hidden"
          objectFit="cover"
          placeholder={{ blurhash: BLUR_HASH }}
          onLoad={onLoad}
        />

        <YStack gap="$100">
          <Heading size="$xl">We couldn't read that label.</Heading>
          <Paragraph color="$textSecondary" size="$s">
            No worries — we were able to match the prescription to the
            following. Is it any of these?
          </Paragraph>

          <Controller
            control={control}
            name="selectedId"
            render={({ field, fieldState }) => (
              <RadioGroup
                dataTrackingId="prescription-unreadable-option"
                value={field.value}
                onValueChange={field.onChange}
                errorText={fieldState.error?.message}
                paddingTop="$100"
                borderless
              >
                {medications.map((med) => {
                  const id = String(
                    med.prescription_details.dispensable_drug_id,
                  );
                  const name =
                    med.prescription_details.display_name ??
                    med.prescription_details.generic_drug_name ??
                    "";
                  return (
                    <RadioGroup.Item
                      key={id}
                      id={`unreadable-med-${id}`}
                      value={id}
                    >
                      {name}
                    </RadioGroup.Item>
                  );
                })}
              </RadioGroup>
            )}
          />
        </YStack>
      </ScrollView>
      <Footer padding="$200" mode="dialog">
        <Button
          mode="primary"
          constrained
          loading={isContinuePending}
          onPress={handleContinue}
          dataTrackingId="prescription-unreadable-continue-button"
        >
          Continue
        </Button>
        <Button
          mode="tertiary"
          onPress={onTryAgain}
          dataTrackingId="prescription-unreadable-try-again-button"
        >
          Try again
        </Button>
      </Footer>
    </YStack>
  );
};
